<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.FILTERS.1
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
  folder: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { isPersonalFavorite, isAccountFavorite } = useKaptionFavorites();

const favoriteForMe = ref(false);
const favoriteForAccount = ref(false);

watch(
  () => props.folder?.id,
  folderId => {
    if (!props.isFolderView || !folderId) return;
    favoriteForMe.value = isPersonalFavorite('folder', folderId);
  },
  { immediate: true }
);

watch(
  () => props.folder?.id,
  folderId => {
    if (!props.isFolderView || !folderId) return;
    favoriteForAccount.value = isAccountFavorite('folder', folderId);
  },
  { immediate: true }
);

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value, {
      favoriteForMe: favoriteForMe.value,
      favoriteForAccount: favoriteForAccount.value,
    });
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
  // NOTICE: KAPTION-INTEGRATION - usage-analytics.FILTERS.1
  // Advanced filter. Emit ONLY the filter KEYS + operators (a bounded set of
  // attribute identifiers) and the count — NEVER the user-entered values, which
  // can be free text (custom attributes, contact names).
  trackUsage(USAGE_ANALYTICS_EVENTS.FILTER_APPLIED, {
    filter_type: 'advanced',
    filter_key: filters.value
      .map(f => f.attributeKey)
      .filter(Boolean)
      .join(','),
    active_filter_count: filters.value.length,
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - rounded-3xl for rounder appearance -->
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-3xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isFolderView">
      <div class="border-b border-n-weak pb-6">
        <Input
          v-model="folderNameLocal"
          :label="t('FILTER.FOLDER_LABEL')"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
        <div class="grid gap-2 mt-3">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="favoriteForMe"
              type="checkbox"
              class="m-0 border border-n-weak rounded-md h-4 w-4"
            />
            <span>{{ t('FILTER.CUSTOM_VIEWS.ADD.FAVORITE_FOR_ME') }}</span>
          </label>
          <label
            v-if="isAdmin"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="favoriteForAccount"
              type="checkbox"
              class="m-0 border border-n-weak rounded-md h-4 w-4"
            />
            <span>{{ t('FILTER.CUSTOM_VIEWS.ADD.FAVORITE_FOR_ACCOUNT') }}</span>
          </label>
        </div>
      </div>
    </div>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
